import React, { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import headerStyles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { SessionContext } from '@benzinga/session-context';

import type { UnusualOptionsCalendarDataSet } from '@benzinga/calendar-manager';
import type { BaseQuoteProfile } from '../../../utils/quote/types';
import { convertToAmPm } from '@benzinga/date-utils';
import { BullSays, BearSays } from '@benzinga/icons';
import { NoFirstRender } from '@benzinga/hooks';
import OptionTabs from '../optionChain/OptionTabs';
import OptionsFilter from './OptionsFilter';
import OptionsTable, { Column } from './OptionsTable';
import { useEdgeRowsGate, EdgeGateCTAOverlay } from '../EdgeRowsGate';
import style from './unusualOptions.module.scss';

// Non-Edge viewers see the first N rows; the rest are blurred behind an upsell.
const UNUSUAL_OPTIONS_VISIBLE_ROWS = 2;

interface UnusualOptionsProps {
  calendarDataSet: UnusualOptionsCalendarDataSet[];
  getCalendarData?: (from_date?: string, to_date?: string, symbol?: string) => void;
  profile: BaseQuoteProfile;
  symbol: string;
}

const calculateDTE = value => {
  return Math.floor((new Date(value).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
};

type ExtendedRow<T, K extends string, V = any> = T & { [P in K]?: V };
type DynamicRow<T> = ExtendedRow<T, string, any>;
const columns: Column<DynamicRow<UnusualOptionsCalendarDataSet>>[] = [
  {
    dataIndex: 'time',
    render: (value, record) => {
      return (
        <div className="flex flex-col">
          <span>{convertToAmPm(value)}</span>
          <span className={style.date}>{dayjs(record?.date).format('MMM DD, YYYY')}</span>
        </div>
      );
    },
    title: 'Date',
  },
  {
    dataIndex: 'put_call',
    render: value => {
      return <div className={style.callPutStyle}>{value}</div>;
    },
    title: 'Put/Call',
  },
  {
    dataIndex: 'strike_price',
    render: (value, record) => {
      const styleClass = record.sentiment?.toLowerCase() === 'bullish' ? style.bullish : style.bearish;
      return <div className={`${style.strikePrice} ${styleClass}`}>${value}</div>;
    },
    title: 'Strike Price',
  },
  {
    dataIndex: 'date_expiration',
    render: value => {
      return dayjs(value).format('MMM DD, YYYY');
    },
    title: 'Exp Date',
  },
  {
    dataIndex: 'date_expiration',
    expectedValue: value => {
      return calculateDTE(value);
    },
    filterType: 'multiSelect',
    filterable: true,

    render: value => {
      return calculateDTE(value);
    },
    title: 'DTE',
  },
  { dataIndex: 'volume', title: 'Trade Vol' },
  { dataIndex: 'size', title: 'Day Vol' },
  {
    dataIndex: 'volOI',
    render: (_, record) => {
      const oi = Number(record?.open_interest);
      const vol = Number(record?.volume);
      return oi > 0 && Number.isFinite(vol) ? ((vol / oi) * 100).toFixed(0) : '—';
    },
    title: 'Vol / OI',
  },
  { dataIndex: 'open_interest', title: 'OI' },
  { dataIndex: 'option_activity_type', title: 'Trade Type' },
  {
    dataIndex: 'sentiment',
    filterType: 'multiSelect',
    filterable: true,
    render: (value: string) => {
      const SentimentIcon = value.toLowerCase() == 'bearish' ? BullSays : BearSays;
      return (
        <div className="flex items-center gap-2">
          <SentimentIcon />
          {value}
        </div>
      );
    },
    title: 'Sentiment',
  },
];

const UnusualOptions = ({ calendarDataSet, profile, symbol }: UnusualOptionsProps) => {
  const [calendarData, setCalendarData] = useState<UnusualOptionsCalendarDataSet[]>(calendarDataSet);
  const [dates, setDates] = useState<string[]>([]);
  const exchange = profile?.richQuoteData?.bzExchange || '';
  const companyName = profile?.richQuoteData?.companyStandardName || '';
  const session = React.useContext(SessionContext);

  const { t } = useTranslation('quote');

  // Gate rows past the first N for non-Edge viewers. The blur + overlay are
  // applied inside OptionsTable against its actually-rendered rows (post
  // client-side filter/sort), so the overlay never covers a table whose
  // visible rows are all un-gated.
  const { isGated } = useEdgeRowsGate(calendarData?.length ?? 0, UNUSUAL_OPTIONS_VISIBLE_ROWS);
  const renderGateOverlay = useCallback(
    (renderedRowCount: number) =>
      renderedRowCount > UNUSUAL_OPTIONS_VISIBLE_ROWS ? (
        <EdgeGateCTAOverlay type="unusual-options-activity" />
      ) : null,
    [],
  );

  const handleDateChange = useCallback(async (dates: [Dayjs, Dayjs] | null) => {
    if (!dates) {
      setDates([]);
      return;
    }
    const [from, to] = dates;
    setDates([from.toISOString(), to.toISOString()]);
  }, []);

  useEffect(() => {
    if (!dates.length) {
      setCalendarData(calendarDataSet);
      return;
    }
    const fetchCalendarData = async () => {
      const [from, to] = dates || [];

      const { getUnusualOptionsCalendarData } = await import('../../../../utils/calendarData');
      const data = await getUnusualOptionsCalendarData({
        session: session.getSession(),
        from_date: from,
        symbol,
        to_date: to,
      });

      if (data?.calendarDataSet) {
        setCalendarData(data.calendarDataSet);
      }
    };
    fetchCalendarData();
  }, [calendarDataSet, dates, session, symbol]);

  return (
    <>
      <OptionTabs activeTab="unusual-options" symbol={symbol} />

      <div className="main-div">
        {!calendarData?.length ? (
          <p className={style.noDataAvailable}>No Unusual Options Activity in the Last 90 Days</p>
        ) : (
          <>
            <div className={headerStyles.header}>
              <div className="title">
                <h2>
                  {t('Quote.OptionChain.unusual-options-activity', {
                    companyStandardName: companyName,
                    symbol: `${exchange}:${symbol}`,
                  })}
                </h2>
              </div>
            </div>
            <div className={style.tableAndFilter}>
              <NoFirstRender fallback={<div className="h-[66px]" />}>
                <OptionsFilter onDateChange={handleDateChange} symbol={symbol} />
              </NoFirstRender>
              <div className={style.tableWrapper}>
                <OptionsTable
                  blurRowsFrom={isGated ? UNUSUAL_OPTIONS_VISIBLE_ROWS : undefined}
                  cellClassName={style.cellStyle}
                  columns={columns}
                  data={calendarData}
                  headerCellClassName={style.cellStyle}
                  renderOverlay={isGated ? renderGateOverlay : undefined}
                  rowClassName={style.rowStyle}
                  tableClassName={style.tableStyle}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default UnusualOptions;
